import { CONNECTED_WALLETS_KEY } from '@/constants/keys'
import { useCallback, useMemo } from 'react'
import { useSessionStorage } from 'usehooks-ts'
import { Address, isAddressEqual } from 'viem'

type Wallets = Record<Address, string>

export function useWallets() {
  const [walletsMap, setWalletsMap] = useSessionStorage<Wallets>(
    CONNECTED_WALLETS_KEY,
    {}
  )
  const wallets = useMemo(
    () => new Map(Object.entries(walletsMap)) as Map<Address, string>,
    [walletsMap]
  )

  const add = useCallback(
    (newWallets: Array<{ address: Address; walletClientType?: string }>) => {
      const next = new Map(wallets)
      newWallets.forEach((wallet) => {
        if (next.has(wallet.address)) return
        next.set(wallet.address, wallet.walletClientType ?? '')
      })
      setWalletsMap(Object.fromEntries(next))
    },
    [setWalletsMap, wallets]
  )

  const remove = useCallback(
    (address: Address) => {
      const next = new Map(wallets)
      next.delete(address)
      setWalletsMap(Object.fromEntries(next))
    },
    [setWalletsMap, wallets]
  )

  const get = useCallback(
    (address: Address) => {
      const wallet = Array.from(wallets.keys()).find((key) =>
        isAddressEqual(key, address)
      )
      if (!wallet) return
      return wallets.get(wallet)
    },
    [wallets]
  )

  return { add, remove, get, wallets: [...wallets.entries()] }
}
